import React, { useState } from 'react';
import { toast } from './Toast';
import { X, MapPin, Users, TrendingUp, AlertCircle, CheckCircle, Lightbulb, Target, Zap, Download, FileText } from 'lucide-react';

interface OptimizationSuggestion {
  category: 'location' | 'competition' | 'pricing' | 'description' | 'purchases' | 'qualifications' | 'documents' | 'other';
  priority: 'high' | 'medium' | 'low';
  title: string;
  problem: string;
  suggestion: string;
  expectedImpact: string;
  implementation: string;
}

interface LocationAnalysis {
  address: string;
  district: string;
  accessibility: string;
  targetAudience: string;
  advantages: string[];
  disadvantages: string[];
  recommendations: string[];
  score: number;
}

interface CompetitionAnalysis {
  numberOfCompetitors: string;
  mainCompetitors: Array<{
    name: string;
    location: string;
    strengths: string[];
    weaknesses: string[];
  }>;
  marketSaturation: string;
  competitiveAdvantages: string[];
  marketGaps: string[];
  recommendations: string[];
  score: number;
}

interface OptimizationReport {
  overallScore: number;
  locationAnalysis: LocationAnalysis;
  competitionAnalysis: CompetitionAnalysis;
  suggestions: OptimizationSuggestion[];
  quickWins: string[];
  priorityActions: string[];
  estimatedPointsIncrease: number;
  summaryText: string;
}

interface OptimizationReportModalProps {
  report: OptimizationReport | null;
  applicationId: number;
  onClose: () => void;
}

export const OptimizationReportModal: React.FC<OptimizationReportModalProps> = ({ report, applicationId, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  if (!report) return null;

  const handleDownloadReport = async (format: 'pdf' | 'txt') => {
    setDownloading(true);
    try {
      // Create formatted text content
      const textContent = formatReportAsText(report);

      if (format === 'txt') {
        // Download as TXT
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Raport_Optymalizacji_${applicationId}_${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        // Download as PDF (via backend)
        const response = await fetch(`/api/crm/applications/${applicationId}/optimization-report/pdf`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ report }),
        });

        if (!response.ok) throw new Error('Failed to generate PDF');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Raport_Optymalizacji_${applicationId}_${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.info('✅ Raport został pobrany!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('❌ Błąd podczas pobierania raportu');
    } finally {
      setDownloading(false);
    }
  };

  const formatReportAsText = (report: OptimizationReport): string => {
    const lines: string[] = [];

    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('         🎯 RAPORT OPTYMALIZACJI WNIOSKU O DOTACJĘ         ');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('');
    lines.push(`Data wygenerowania: ${new Date().toLocaleString('pl-PL')}`);
    lines.push(`ID wniosku: ${applicationId}`);
    lines.push('');
    lines.push('───────────────────────────────────────────────────────────');
    lines.push(' OGÓLNA OCENA WNIOSKU');
    lines.push('───────────────────────────────────────────────────────────');
    lines.push(`Punktacja: ${report.overallScore}/100`);
    lines.push('');
    lines.push('📋 PODSUMOWANIE:');
    lines.push(report.summaryText);
    lines.push('');

    if (report.estimatedPointsIncrease > 0) {
      lines.push('───────────────────────────────────────────────────────────');
      lines.push(' 📈 POTENCJALNY WZROST PUNKTACJI');
      lines.push('───────────────────────────────────────────────────────────');
      lines.push(`+${report.estimatedPointsIncrease} punktów po wdrożeniu sugestii`);
      lines.push('');
    }

    lines.push('───────────────────────────────────────────────────────────');
    lines.push(' ⚡ SZYBKIE WYGRANE (1-2 dni)');
    lines.push('───────────────────────────────────────────────────────────');
    report.quickWins.forEach((win, idx) => {
      lines.push(`${idx + 1}. ${win}`);
    });
    lines.push('');

    lines.push('───────────────────────────────────────────────────────────');
    lines.push(' 🎯 PLAN DZIAŁANIA (PRIORYTETOWY)');
    lines.push('───────────────────────────────────────────────────────────');
    report.priorityActions.forEach((action, idx) => {
      lines.push(`${idx + 1}. ${action}`);
    });
    lines.push('');

    lines.push('───────────────────────────────────────────────────────────');
    lines.push(' 📍 ANALIZA LOKALIZACJI');
    lines.push('───────────────────────────────────────────────────────────');
    lines.push(`Ocena: ${report.locationAnalysis.score}/10`);
    lines.push(`Adres: ${report.locationAnalysis.address}`);
    lines.push(`Dzielnica: ${report.locationAnalysis.district}`);
    lines.push(`Dostępność: ${report.locationAnalysis.accessibility}`);
    lines.push(`Grupa docelowa: ${report.locationAnalysis.targetAudience}`);
    lines.push('');
    lines.push('✅ ZALETY:');
    report.locationAnalysis.advantages.forEach(adv => lines.push(`  + ${adv}`));
    lines.push('');
    lines.push('⚠️ WADY:');
    report.locationAnalysis.disadvantages.forEach(dis => lines.push(`  - ${dis}`));
    lines.push('');
    lines.push('💡 REKOMENDACJE:');
    report.locationAnalysis.recommendations.forEach(rec => lines.push(`  • ${rec}`));
    lines.push('');

    lines.push('───────────────────────────────────────────────────────────');
    lines.push(' 👥 ANALIZA KONKURENCJI');
    lines.push('───────────────────────────────────────────────────────────');
    lines.push(`Ocena: ${report.competitionAnalysis.score}/10`);
    lines.push(`Liczba konkurentów: ${report.competitionAnalysis.numberOfCompetitors}`);
    lines.push(`Nasycenie rynku: ${report.competitionAnalysis.marketSaturation}`);
    lines.push('');

    if (report.competitionAnalysis.mainCompetitors.length > 0) {
      lines.push('GŁÓWNI KONKURENCI:');
      report.competitionAnalysis.mainCompetitors.forEach((comp, idx) => {
        lines.push(`\n${idx + 1}. ${comp.name} (${comp.location})`);
        lines.push('   Mocne strony:');
        comp.strengths.forEach(s => lines.push(`     + ${s}`));
        lines.push('   Słabe strony:');
        comp.weaknesses.forEach(w => lines.push(`     - ${w}`));
      });
      lines.push('');
    }

    lines.push('💪 TWOJE PRZEWAGI:');
    report.competitionAnalysis.competitiveAdvantages.forEach(adv => lines.push(`  • ${adv}`));
    lines.push('');
    lines.push('🔍 LUKI RYNKOWE:');
    report.competitionAnalysis.marketGaps.forEach(gap => lines.push(`  • ${gap}`));
    lines.push('');
    lines.push('💡 JAK SIĘ WYRÓŻNIĆ:');
    report.competitionAnalysis.recommendations.forEach(rec => lines.push(`  • ${rec}`));
    lines.push('');

    lines.push('═══════════════════════════════════════════════════════════');
    lines.push(' 💡 SZCZEGÓŁOWE SUGESTIE OPTYMALIZACJI');
    lines.push('═══════════════════════════════════════════════════════════');
    report.suggestions.forEach((suggestion, idx) => {
      lines.push('');
      lines.push(`${idx + 1}. ${suggestion.title.toUpperCase()}`);
      lines.push(`   Kategoria: ${suggestion.category} | Priorytet: ${suggestion.priority.toUpperCase()}`);
      lines.push('');
      lines.push('   ❌ Problem:');
      lines.push(`      ${suggestion.problem}`);
      lines.push('');
      lines.push('   💡 Sugestia:');
      lines.push(`      ${suggestion.suggestion}`);
      lines.push('');
      lines.push('   ✅ Oczekiwany Efekt:');
      lines.push(`      ${suggestion.expectedImpact}`);
      lines.push('');
      lines.push('   🛠️ Jak to Zrealizować:');
      lines.push(`      ${suggestion.implementation}`);
      lines.push('');
      lines.push('───────────────────────────────────────────────────────────');
    });

    lines.push('');
    lines.push('═══════════════════════════════════════════════════════════');
    lines.push('Raport wygenerowany przez AI Assistant (Claude Sonnet 4.5)');
    lines.push('System CRM Fundacji Promocji Nowej Huty');
    lines.push('═══════════════════════════════════════════════════════════');

    return lines.join('\n');
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'location': return <MapPin size={18} />;
      case 'competition': return <Users size={18} />;
      case 'pricing': return <TrendingUp size={18} />;
      case 'qualifications': return <Target size={18} />;
      default: return <Lightbulb size={18} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'location': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'competition': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'pricing': return 'bg-green-100 text-green-700 border-green-300';
      case 'qualifications': return 'bg-orange-100 text-orange-700 border-orange-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-700 rounded">WYSOKI</span>;
      case 'medium':
        return <span className="px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded">ŚREDNI</span>;
      case 'low':
        return <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded">NISKI</span>;
      default:
        return null;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const getOverallScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (score >= 60) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (score >= 40) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg flex justify-between items-start z-10">
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2">🎯 Raport Optymalizacji Wniosku</h2>
            <p className="text-blue-100 text-sm">Analiza AI z konkretnymi sugestiami usprawnienia wniosku o dotację</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors ml-4"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Overall Score */}
          <div className={`border-2 rounded-lg p-6 ${getOverallScoreColor(report.overallScore)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold mb-1">Ogólna Ocena Wniosku</h3>
                <p className="text-sm opacity-80">Analiza wszystkich aspektów wniosku</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold">{report.overallScore}</div>
                <div className="text-sm font-medium">/ 100 pkt</div>
              </div>
            </div>
          </div>

          {/* Quick Summary */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-blue-900 mb-3">📋 Podsumowanie</h3>
            <p className="text-sm text-blue-800 whitespace-pre-line leading-relaxed">{report.summaryText}</p>
          </div>

          {/* Estimated Points Increase */}
          {report.estimatedPointsIncrease > 0 && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
              <div className="flex items-center">
                <TrendingUp className="text-green-600 mr-3" size={32} />
                <div>
                  <h3 className="text-lg font-bold text-green-900">Potencjalny Wzrost Punktacji</h3>
                  <p className="text-green-700 text-sm">
                    Wdrożenie sugestii może dodać <strong className="text-2xl">+{report.estimatedPointsIncrease} pkt</strong> do Twojej punktacji
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Wins */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Zap className="text-yellow-600 mr-2" size={24} />
              <h3 className="text-lg font-bold text-yellow-900">⚡ Szybkie Wygrane</h3>
            </div>
            <p className="text-sm text-yellow-800 mb-3">Rzeczy które możesz poprawić NATYCHMIAST (1-2 dni):</p>
            <ul className="space-y-2">
              {report.quickWins.map((win, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm text-yellow-900">{win}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Priority Actions */}
          <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Target className="text-purple-600 mr-2" size={24} />
              <h3 className="text-lg font-bold text-purple-900">🎯 Plan Działania (Priorytetowy)</h3>
            </div>
            <p className="text-sm text-purple-800 mb-3">Najważniejsze działania w kolejności:</p>
            <ol className="space-y-2">
              {report.priorityActions.map((action, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-purple-900">{action}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Location Analysis */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <MapPin className="text-blue-600 mr-2" size={24} />
                <h3 className="text-lg font-bold text-gray-900">📍 Analiza Lokalizacji</h3>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Ocena:</span>
                <span className={`text-2xl font-bold ${getScoreColor(report.locationAnalysis.score)}`}>
                  {report.locationAnalysis.score}/10
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">ADRES</p>
                <p className="text-sm font-medium">{report.locationAnalysis.address}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">DZIELNICA</p>
                <p className="text-sm font-medium">{report.locationAnalysis.district}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">DOSTĘPNOŚĆ</p>
                <p className="text-sm text-gray-700">{report.locationAnalysis.accessibility}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">GRUPA DOCELOWA</p>
                <p className="text-sm text-gray-700">{report.locationAnalysis.targetAudience}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-xs font-semibold text-green-600 mb-2">✅ ZALETY</p>
                <ul className="space-y-1">
                  {report.locationAnalysis.advantages.map((adv, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="text-green-500 mr-2">+</span>
                      {adv}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-red-600 mb-2">⚠️ WADY</p>
                <ul className="space-y-1">
                  {report.locationAnalysis.disadvantages.map((dis, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start">
                      <span className="text-red-500 mr-2">-</span>
                      {dis}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-4 bg-blue-50 rounded p-3">
              <p className="text-xs font-semibold text-blue-700 mb-2">💡 REKOMENDACJE</p>
              <ul className="space-y-1">
                {report.locationAnalysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm text-blue-800">{rec}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Competition Analysis */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Users className="text-purple-600 mr-2" size={24} />
                <h3 className="text-lg font-bold text-gray-900">👥 Analiza Konkurencji</h3>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Ocena:</span>
                <span className={`text-2xl font-bold ${getScoreColor(report.competitionAnalysis.score)}`}>
                  {report.competitionAnalysis.score}/10
                </span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-1">LICZBA KONKURENTÓW</p>
              <p className="text-sm font-medium">{report.competitionAnalysis.numberOfCompetitors}</p>
            </div>

            {report.competitionAnalysis.mainCompetitors.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 mb-3">GŁÓWNI KONKURENCI</p>
                <div className="space-y-3">
                  {report.competitionAnalysis.mainCompetitors.map((comp, idx) => (
                    <div key={idx} className="bg-gray-50 rounded p-3">
                      <p className="font-semibold text-sm mb-1">{comp.name}</p>
                      <p className="text-xs text-gray-600 mb-2">📍 {comp.location}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-green-600 font-semibold">Mocne strony:</span>
                          <ul className="mt-1 space-y-0.5">
                            {comp.strengths.map((s, i) => (
                              <li key={i} className="text-gray-700">+ {s}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="text-red-600 font-semibold">Słabe strony:</span>
                          <ul className="mt-1 space-y-0.5">
                            {comp.weaknesses.map((w, i) => (
                              <li key={i} className="text-gray-700">- {w}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-500 mb-1">NASYCENIE RYNKU</p>
              <p className="text-sm text-gray-700">{report.competitionAnalysis.marketSaturation}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 rounded p-3">
                <p className="text-xs font-semibold text-green-700 mb-2">💪 TWOJE PRZEWAGI</p>
                <ul className="space-y-1">
                  {report.competitionAnalysis.competitiveAdvantages.map((adv, idx) => (
                    <li key={idx} className="text-sm text-green-800">{adv}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-orange-50 rounded p-3">
                <p className="text-xs font-semibold text-orange-700 mb-2">🔍 LUKI RYNKOWE</p>
                <ul className="space-y-1">
                  {report.competitionAnalysis.marketGaps.map((gap, idx) => (
                    <li key={idx} className="text-sm text-orange-800">{gap}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-4 bg-purple-50 rounded p-3">
              <p className="text-xs font-semibold text-purple-700 mb-2">💡 JAK SIĘ WYRÓŻNIĆ</p>
              <ul className="space-y-1">
                {report.competitionAnalysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm text-purple-800">{rec}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Detailed Suggestions */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">💡 Szczegółowe Sugestie Optymalizacji</h3>
            <div className="space-y-4">
              {report.suggestions.map((suggestion, idx) => (
                <div key={idx} className="border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center flex-1">
                      <div className={`p-2 rounded-lg mr-3 border-2 ${getCategoryColor(suggestion.category)}`}>
                        {getCategoryIcon(suggestion.category)}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                        <p className="text-xs text-gray-500 mt-0.5 capitalize">{suggestion.category}</p>
                      </div>
                    </div>
                    {getPriorityBadge(suggestion.priority)}
                  </div>

                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-semibold text-red-700 mb-1">❌ Problem:</p>
                      <p className="text-gray-700">{suggestion.problem}</p>
                    </div>

                    <div>
                      <p className="font-semibold text-blue-700 mb-1">💡 Sugestia:</p>
                      <p className="text-gray-700">{suggestion.suggestion}</p>
                    </div>

                    <div>
                      <p className="font-semibold text-green-700 mb-1">✅ Oczekiwany Efekt:</p>
                      <p className="text-gray-700">{suggestion.expectedImpact}</p>
                    </div>

                    <div className="bg-gray-50 rounded p-3">
                      <p className="font-semibold text-purple-700 mb-1">🛠️ Jak to Zrealizować:</p>
                      <p className="text-gray-700">{suggestion.implementation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-4 border-t">
            <p>Raport wygenerowany automatycznie przez AI Assistant (Claude Sonnet 4.5)</p>
            <p className="mt-1">System CRM Fundacji Promocji Nowej Huty</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-between items-center rounded-b-lg">
          <div className="flex gap-3">
            <button
              onClick={() => handleDownloadReport('txt')}
              disabled={downloading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FileText size={18} />
              {downloading ? 'Pobieranie...' : 'Pobierz TXT'}
            </button>
            <button
              onClick={() => handleDownloadReport('pdf')}
              disabled={downloading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Download size={18} />
              {downloading ? 'Pobieranie...' : 'Pobierz PDF'}
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Zamknij Raport
          </button>
        </div>
      </div>
    </div>
  );
};
